import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cajaService } from '../../services/api';
import { useMetodosPago } from '../../hooks/useMetodosPago';
import { useCategoriasMovimiento } from '../../hooks/useCategoriasMovimiento';
import { btnSecundario, money, segmento, segmentos } from '../../components/ui/estilos';
import { delta, usePeriodo } from './periodo';
import { Barras, Encabezado, Kpis, Progreso, Tabla, Tarjeta } from './ui';
import { num, useReporte } from './useReporte';
import { exportarExcel, exportarPDF, tipoMovLabel } from './exportar';

const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const TOP = 10;
// Cada bloque del reporte en su pestaña; ?t= para que Resumen enlace directo.
type Pestana = 'ventas' | 'metodos' | 'productos' | 'movimientos';
const PESTANAS: [Pestana, string][] = [['ventas', 'Ventas'], ['metodos', 'Métodos de pago'], ['productos', 'Productos'], ['movimientos', 'Movimientos']];
const pct = (v: number, t: number) => `${t ? Math.round(v / t * 100) : 0}%`;
const chico: React.CSSProperties = { ...btnSecundario, height: 38, fontSize: 14, padding: '0 12px' };

export default function Ventas() {
  const { label } = useMetodosPago();
  const { label: catLabel } = useCategoriasMovimiento();
  const p = usePeriodo('semana');
  const { datos, error } = useReporte(cajaService.getReporteCompleto, p.actual, p.previo);
  const horas = useReporte(cajaService.getReporteClientes, p.actual, p.previo);
  const [menos, setMenos] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab = (PESTANAS.find(([k]) => k === params.get('t'))?.[0] || 'ventas') as Pestana;

  const a = datos?.actual, r = a?.resumen, rp = datos?.previo?.resumen;
  const netas = num(r?.total_ventas), desc = num(r?.total_descuentos), brutas = netas + desc;
  const brutasPrev = num(rp?.total_ventas) + num(rp?.total_descuentos);

  const porDia = (a?.por_dia || []).map((d: any, i: number, arr: any[]) => {
    const f = new Date(`${String(d.fecha).slice(0, 10)}T12:00:00`);
    return { label: arr.length > 7 ? String(f.getDate()) : DIAS[f.getDay()], valor: num(d.ventas), destacada: i === arr.length - 1 };
  });
  const porHora = (horas.datos?.actual?.por_hora || []).map((h: any) => ({ label: `${h.hora}h`, valor: num(h.total) }));

  const productos: any[] = a?.top_productos || [];
  const totalProductos = productos.reduce((s, x) => s + num(x.total_vendido), 0);
  const porCategoria: Record<string, number> = {};
  productos.forEach(x => { porCategoria[x.categoria] = (porCategoria[x.categoria] || 0) + num(x.total_vendido); });
  const categorias = Object.entries(porCategoria)
    .filter(([, v]) => v > 0).sort((x, y) => y[1] - x[1]);
  // Menos vendidos incluye los que no se vendieron nada: es lo que más interesa ver.
  const lista = menos
    ? [...productos].sort((x, y) => x.unidades - y.unidades || num(x.total_vendido) - num(y.total_vendido)).slice(0, TOP)
    : [...productos].filter(x => x.unidades > 0).sort((x, y) => num(y.total_vendido) - num(x.total_vendido)).slice(0, TOP);

  const metodos: any[] = a?.por_metodo || [];
  const totalMetodos = metodos.reduce((s, m) => s + num(m.total), 0);
  const mov = a?.movimientos;
  const [ini, fin] = p.actual;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Encabezado titulo="Ventas" p={p} extra={datos && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => exportarPDF(a, ini, fin, catLabel)} style={chico}>Exportar PDF</button>
          <button onClick={() => exportarExcel(a, ini, fin, label, catLabel)} style={chico}>Exportar Excel</button>
        </div>
      )} />
      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}
      {!datos && !error && <div style={{ color: 'var(--muted)' }}>Cargando…</div>}
      <div role="tablist" aria-label="Sección" style={{ ...segmentos, width: 'fit-content', maxWidth: '100%', overflowX: 'auto' }}>
        {PESTANAS.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setParams(k === 'ventas' ? {} : { t: k }, { replace: true })}
            style={{ ...segmento(tab === k), height: 40, fontSize: 15, whiteSpace: 'nowrap' }}>{l}</button>
        ))}
      </div>
      {datos && tab === 'ventas' && <>
        <Kpis items={[
          { label: 'Ventas brutas', valor: money(brutas), delta: delta(brutas, brutasPrev, p.periodo) },
          { label: 'Descuentos', valor: money(desc), delta: { texto: `${pct(desc, brutas)} de ventas`, color: 'var(--muted)' } },
          { label: 'Ventas netas', valor: money(netas), delta: delta(netas, num(rp?.total_ventas), p.periodo) },
          { label: 'Ticket promedio', valor: money(num(r?.ticket_promedio)), delta: delta(num(r?.ticket_promedio), num(rp?.ticket_promedio), p.periodo) },
          { label: 'Tickets', valor: String(r?.total_comandas || 0), delta: delta(num(r?.total_comandas), num(rp?.total_comandas), p.periodo) },
          { label: 'Utilidad estimada', valor: money(num(r?.utilidad)), delta: { texto: `costo ${money(num(r?.total_costo))}`, color: 'var(--muted)' } },
        ]} />

        <Tarjeta titulo="Ventas por día" gap={14} extra={<span style={{ fontSize: 14, color: 'var(--muted)' }}>{money(netas)}</span>}>
          <Barras datos={porDia} alto={180} formato={money} />
        </Tarjeta>

        <Tarjeta titulo="Ventas por hora">
          {horas.error ? <div style={{ color: 'var(--muted)' }}>{horas.error}</div> : <Barras datos={porHora} alto={140} formato={money} sinValores />}
        </Tarjeta>
      </>}

      {datos && tab === 'metodos' && <>

        <Tarjeta titulo="Métodos de pago y propinas">
          <Tabla columnas={[{ t: 'MÉTODO', w: 'minmax(0,1fr)' }, { t: 'VENTAS', w: '110px', der: true }, { t: '% DEL TOTAL', w: '100px', der: true }, { t: 'PROPINAS', w: '100px', der: true }]}
            filas={metodos.map(m => [label(m.metodo), <b>{money(num(m.total))}</b>, <span style={{ color: 'var(--muted)' }}>{pct(num(m.total), totalMetodos)}</span>, money(num(m.propinas))])} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, paddingTop: 6, fontSize: 15, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--muted)' }}>Total propinas <b style={{ color: 'var(--ink)' }}>{money(num(r?.total_propinas))}</b></span>
            <span style={{ color: 'var(--muted)' }}>Ya entregadas <b style={{ color: 'var(--ink)' }}>{money(num(mov?.total_propinas_pagadas))}</b></span>
          </div>
        </Tarjeta>

      </>}

      {datos && tab === 'productos' && <>
        <Tarjeta titulo={menos ? 'Productos menos vendidos' : 'Productos más vendidos'}
          extra={<button onClick={() => setMenos(m => !m)} style={{ ...chico, height: 34, fontSize: 13 }}>{menos ? 'Ver más vendidos' : 'Ver menos vendidos'}</button>}>
          <Tabla columnas={[{ t: '', w: '28px' }, { t: 'PRODUCTO', w: 'minmax(0,1fr)' }, { t: 'CANT.', w: '70px', der: true }, { t: 'TOTAL', w: '100px', der: true }, { t: '% VENTAS', w: '80px', der: true }]}
            filas={lista.map((x, i) => [<span style={{ color: 'var(--muted)', fontWeight: 700 }}>{i + 1}</span>, x.producto, `${x.unidades} u`, <b>{money(num(x.total_vendido))}</b>, <span style={{ color: 'var(--muted)' }}>{pct(num(x.total_vendido), totalProductos)}</span>])} />
        </Tarjeta>

        <Tarjeta titulo="Ventas por categoría">
          {!categorias.length && <div style={{ color: 'var(--muted)' }}>Sin ventas en este periodo.</div>}
          {categorias.map(([c, v]) => <Progreso key={c} label={c} valor={money(v)} pct={v / (totalProductos || 1) * 100} />)}
        </Tarjeta>
      </>}

      {datos && tab === 'movimientos' && <>
        <Tarjeta titulo="Movimientos de caja" extra={<span style={{ fontSize: 14, color: 'var(--muted)' }}>entró {money(num(mov?.total_depositos))} · salió {money(num(mov?.total_retiros))}</span>}>
          <Tabla vacio="Sin entradas ni salidas de efectivo." columnas={[{ t: 'CONCEPTO', w: 'minmax(0,1fr)' }, { t: 'TIPO', w: '90px' }, { t: 'MOVS.', w: '70px', der: true }, { t: 'TOTAL', w: '110px', der: true }]}
            filas={(mov?.por_categoria || []).map((m: any) => [catLabel(m.categoria), tipoMovLabel(m.tipo), m.cantidad, <b>{money(num(m.total))}</b>])} />
        </Tarjeta>
      </>}
    </div>
  );
}
